// Port of Offline/TileMath.swift (TrailBivvyKit): Web Mercator (EPSG:3857) XYZ tile addresses and tile arithmetic.
//
// TileCoordinate is a value type in Swift (Hashable); here it is a plain `{ z, x, y }` object. Sets of tiles are keyed
// by `tileKey` ("z/x/y"), which is also the Swift `description`.

import type { GeoBounds, GeoCoordinate } from '../core/models';
import { boundsExpanded, boundsOf, degreesToRadians, isValidCoordinate, localXY, radiansToDegrees, simplify } from '../core/geo';

/**
 * A Web Mercator XYZ tile address: zoom `z`, column `x` (west → east) and row `y` (north → south). This is the
 * "Google"/OSM scheme used by Leaflet and by the geo.admin.ch WMTS `3857` matrix sets, so no y-flip is ever needed.
 */
export interface TileCoordinate {
  /** Zoom level (0 = one tile for the whole world). */
  z: number;
  /** Column, 0 at 180° W. */
  x: number;
  /** Row, 0 at the northern edge (≈ 85.05° N). */
  y: number;
}

export function tileCoordinate(z: number, x: number, y: number): TileCoordinate {
  return { z, x, y };
}

/** "z/x/y" (Swift `description`); also the key of tile sets and maps. */
export function tileKey(tile: TileCoordinate): string {
  return `${tile.z}/${tile.x}/${tile.y}`;
}

export function sameTile(a: TileCoordinate, b: TileCoordinate): boolean {
  return a.z === b.z && a.x === b.x && a.y === b.y;
}

/** `true` when the address exists in the 2^z × 2^z grid. */
export function tileIsValid(tile: TileCoordinate): boolean {
  if (!Number.isInteger(tile.z) || !Number.isInteger(tile.x) || !Number.isInteger(tile.y)) return false;
  if (tile.z < 0 || tile.z > TileMath.maximumZoom) return false;
  const n = 2 ** tile.z;
  return tile.x >= 0 && tile.y >= 0 && tile.x < n && tile.y < n;
}

/** The tile at a lower (or equal) zoom level that contains this tile, or undefined when `zoom` is out of range. */
export function tileAncestor(tile: TileCoordinate, zoom: number): TileCoordinate | undefined {
  if (zoom < 0 || zoom > tile.z) return undefined;
  const factor = 2 ** (tile.z - zoom);
  return { z: zoom, x: Math.floor(tile.x / factor), y: Math.floor(tile.y / factor) };
}

/** Rectangle in a local metric plane (TileMath internals). */
export interface PlaneRect {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Web Mercator XYZ tile arithmetic (EPSG:3857, 256 px tiles). */
export const TileMath = {
  /** Latitude limit of the Web Mercator projection, in degrees. */
  maximumLatitude: 85.051_128_779_806_59,
  /** Highest zoom level handled (keeps 2^z exact and far from overflow). */
  maximumZoom: 30,
  /** Equatorial circumference of the WGS84 ellipsoid used by EPSG:3857, in metres. */
  earthCircumference: 40_075_016.685_578_5,
  /**
   * Simplification tolerance applied to corridor polylines before the tile search, in metres. The search radius is
   * widened by the same amount, so the result is always a superset of the exact corridor.
   */
  corridorSimplificationTolerance: 10,

  /** The tile containing `c` at `zoom` (clamped to the valid grid; latitude clamped to ±85.05°). */
  tile(c: GeoCoordinate, zoom: number): TileCoordinate {
    const z = TileMath.clampedZoom(zoom);
    const n = 2 ** z;
    const f = TileMath.fractionalTile(c.latitude, c.longitude, z);
    return { z, x: TileMath.index(f.x, n), y: TileMath.index(f.y, n) };
  },

  /** Geographic extent of a tile. */
  bounds(tile: TileCoordinate): GeoBounds {
    const n = 2 ** TileMath.clampedZoom(tile.z);
    const longitude = (x: number) => (x / n) * 360 - 180;
    const latitude = (y: number) => radiansToDegrees(Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n))));
    return {
      minLatitude: latitude(tile.y + 1),
      minLongitude: longitude(tile.x),
      maxLatitude: latitude(tile.y),
      maxLongitude: longitude(tile.x + 1),
    };
  },

  /**
   * All tiles intersecting `bounds` at `zoom`, row by row (north → south, west → east). A bounds edge lying exactly on
   * a tile edge does not pull in the neighbouring tile, so `tiles(covering: bounds(of: t), zoom: t.z) == [t]`.
   */
  tilesCovering(bounds: GeoBounds, zoom: number): TileCoordinate[] {
    const range = TileMath.tileRange(bounds, zoom);
    if (!range) return [];
    const result: TileCoordinate[] = [];
    for (let y = range.minY; y <= range.maxY; y++) {
      for (let x = range.minX; x <= range.maxX; x++) result.push({ z: range.z, x, y });
    }
    return result;
  },

  /**
   * Tiles whose area comes within `halfWidthMeters` of the polyline through `coordinates` (a buffered corridor).
   *
   * The polyline is simplified (10 m tolerance) and each candidate tile is tested exactly against every segment in a
   * local planar projection, so the count grows monotonically with the width and with the zoom level. A single
   * coordinate yields the tiles within `halfWidthMeters` of that point. Invalid coordinates are ignored.
   * Returns unique tiles (Swift `Set<TileCoordinate>`), in discovery order.
   */
  tilesAlongCorridor(coordinates: readonly GeoCoordinate[], halfWidthMeters: number, zoom: number): TileCoordinate[] {
    const valid = coordinates.filter(isValidCoordinate);
    const first = valid[0];
    if (!first) return [];
    const z = TileMath.clampedZoom(zoom);
    const halfWidth = Number.isFinite(halfWidthMeters) ? Math.max(0, halfWidthMeters) : 0;
    const tolerance = TileMath.corridorSimplificationTolerance;
    const simplified = simplify(
      valid.map((coordinate) => ({ coordinate })),
      tolerance,
    ).map((p) => p.coordinate);
    const reach = halfWidth + tolerance;

    const segments: Array<[GeoCoordinate, GeoCoordinate]> = [];
    if (simplified.length < 2) {
      segments.push([first, first]);
    } else {
      for (let i = 1; i < simplified.length; i++) segments.push([simplified[i - 1], simplified[i]]);
    }

    const seen = new Set<string>();
    const result: TileCoordinate[] = [];
    for (const [a, b] of segments) {
      const segmentBounds = boundsOf([a, b]);
      if (!segmentBounds) continue;
      // Generous candidate box (+50 m) — the exact distance test below decides.
      const search = boundsExpanded(segmentBounds, reach + 50);
      for (const tile of TileMath.tilesCovering(search, z)) {
        const key = tileKey(tile);
        if (seen.has(key)) continue;
        if (TileMath.distanceFromSegmentToTile(a, b, tile) <= reach) {
          seen.add(key);
          result.push(tile);
        }
      }
    }
    return result;
  },

  /** Ground width of one tile at `latitude` and `zoom`, in metres. */
  tileWidthMeters(latitude: number, zoom: number): number {
    const lat = Math.min(TileMath.maximumLatitude, Math.max(-TileMath.maximumLatitude, latitude));
    return (TileMath.earthCircumference * Math.cos(degreesToRadians(lat))) / 2 ** TileMath.clampedZoom(zoom);
  },

  // MARK: - Internals

  clampedZoom(zoom: number): number {
    const z = Number.isFinite(zoom) ? Math.trunc(zoom) : 0;
    return Math.min(TileMath.maximumZoom, Math.max(0, z));
  },

  /** Continuous tile-space position (tile units) of a coordinate. */
  fractionalTile(latitude: number, longitude: number, zoom: number): { x: number; y: number } {
    const n = 2 ** TileMath.clampedZoom(zoom);
    const lon = Number.isFinite(longitude) ? Math.min(180, Math.max(-180, longitude)) : 0;
    const lat = Number.isFinite(latitude) ? Math.min(TileMath.maximumLatitude, Math.max(-TileMath.maximumLatitude, latitude)) : 0;
    const x = ((lon + 180) / 360) * n;
    const phi = degreesToRadians(lat);
    const y = ((1 - Math.log(Math.tan(phi) + 1 / Math.cos(phi)) / Math.PI) / 2) * n;
    return { x, y };
  },

  /** Floors a tile-space value to a valid index in 0..<count (safe for NaN/huge values). */
  index(value: number, count: number): number {
    if (!Number.isFinite(value)) return 0;
    return Math.min(count - 1, Math.max(0, Math.floor(value)));
  },

  tileRange(bounds: GeoBounds, zoom: number): { z: number; minX: number; maxX: number; minY: number; maxY: number } | undefined {
    const values = [bounds.minLatitude, bounds.maxLatitude, bounds.minLongitude, bounds.maxLongitude];
    if (!values.every(Number.isFinite)) return undefined;
    if (bounds.minLatitude > bounds.maxLatitude || bounds.minLongitude > bounds.maxLongitude) return undefined;
    const z = TileMath.clampedZoom(zoom);
    const n = 2 ** z;
    const northWest = TileMath.fractionalTile(bounds.maxLatitude, bounds.minLongitude, z);
    const southEast = TileMath.fractionalTile(bounds.minLatitude, bounds.maxLongitude, z);
    // Nudge inwards so edges lying exactly on tile boundaries do not include the neighbour.
    const epsilon = 1e-9;
    const minX = TileMath.index(northWest.x + epsilon, n);
    const maxX = Math.max(minX, TileMath.index(southEast.x - epsilon, n));
    const minY = TileMath.index(northWest.y + epsilon, n);
    const maxY = Math.max(minY, TileMath.index(southEast.y - epsilon, n));
    return { z, minX, maxX, minY, maxY };
  },

  /**
   * Distance in metres between segment a→b and the tile's rectangle (0 when they intersect), computed in a local
   * equirectangular plane centred on `a` (accurate for corridor-scale distances).
   */
  distanceFromSegmentToTile(a: GeoCoordinate, b: GeoCoordinate, tile: TileCoordinate): number {
    const box = TileMath.bounds(tile);
    const southWest = localXY({ latitude: box.minLatitude, longitude: box.minLongitude }, a);
    const northEast = localXY({ latitude: box.maxLatitude, longitude: box.maxLongitude }, a);
    const rect = { minX: southWest.x, minY: southWest.y, maxX: northEast.x, maxY: northEast.y };
    const end = localXY(b, a);
    return TileMath.segmentRectangleDistance(0, 0, end.x, end.y, rect);
  },

  segmentRectangleDistance(ax: number, ay: number, bx: number, by: number, rect: PlaneRect): number {
    if (TileMath.segmentIntersectsRectangle(ax, ay, bx, by, rect)) return 0;
    const pointToRect = (px: number, py: number) => {
      const dx = Math.max(rect.minX - px, 0, px - rect.maxX);
      const dy = Math.max(rect.minY - py, 0, py - rect.maxY);
      return Math.sqrt(dx * dx + dy * dy);
    };
    const pointToSegment = (px: number, py: number) => {
      const dx = bx - ax;
      const dy = by - ay;
      const length2 = dx * dx + dy * dy;
      let t = 0;
      if (length2 > 0) t = Math.min(1, Math.max(0, ((px - ax) * dx + (py - ay) * dy) / length2));
      const cx = ax + t * dx - px;
      const cy = ay + t * dy - py;
      return Math.sqrt(cx * cx + cy * cy);
    };
    let best = Math.min(pointToRect(ax, ay), pointToRect(bx, by));
    const corners: Array<[number, number]> = [
      [rect.minX, rect.minY],
      [rect.minX, rect.maxY],
      [rect.maxX, rect.minY],
      [rect.maxX, rect.maxY],
    ];
    for (const [cx, cy] of corners) best = Math.min(best, pointToSegment(cx, cy));
    return best;
  },

  /** Liang–Barsky clip test. */
  segmentIntersectsRectangle(ax: number, ay: number, bx: number, by: number, rect: PlaneRect): boolean {
    const dx = bx - ax;
    const dy = by - ay;
    let t0 = 0;
    let t1 = 1;
    const checks: Array<[number, number]> = [
      [-dx, ax - rect.minX],
      [dx, rect.maxX - ax],
      [-dy, ay - rect.minY],
      [dy, rect.maxY - ay],
    ];
    for (const [p, q] of checks) {
      if (p === 0) {
        if (q < 0) return false;
      } else {
        const r = q / p;
        if (p < 0) {
          if (r > t1) return false;
          if (r > t0) t0 = r;
        } else {
          if (r < t0) return false;
          if (r < t1) t1 = r;
        }
      }
    }
    return true;
  },
};
